import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Spinner } from 'reactstrap';
import { motion } from 'framer-motion';
import api from '../helpers/api';

const TransferModal = ({ 
    isOpen, 
    toggle, 
    selectedAssets, 
    onTransferComplete, 
    currentOrganizationId 
}) => {
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState('');
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');

    // Fetch organizations
    const fetchOrganizations = async () => {
        try {
            const { data } = await api.get('organization/all');
            setOrganizations(data.response || []);
        } catch (error) {
            console.error('Error fetching organizations:', error);
            setApiError('Failed to fetch organizations');
        }
    };

    // Fetch locations based on selected organization
    const fetchLocations = async (organizationId) => {
        if (!organizationId) {
            setLocations([]);
            return;
        }
        
        try {
            const { data } = await api.get(`organization-location/get-by-organization-id?organizationId=${organizationId}&count=9999`);
            setLocations(data.response[0]?.locations || []);
            setSelectedLocation(''); // Reset location when organization changes
        } catch (error) {
            console.error('Error fetching locations:', error);
            setLocations([]);
        }
    };

    // Handle organization selection
    const handleOrganizationChange = (e) => {
        const organizationId = e.target.value;
        setSelectedOrganization(organizationId);
        fetchLocations(organizationId);
        setErrors(prev => ({ ...prev, organization: '' }));
    };

    // Handle location selection
    const handleLocationChange = (e) => {
        setSelectedLocation(e.target.value);
        setErrors(prev => ({ ...prev, location: '' }));
    };

    // Handle transfer submission
  const handleTransfer = async () => {
    const newErrors = {};
    
    if (!selectedOrganization) {
        newErrors.organization = 'Please select a organization';
    }
    if (!selectedLocation) {
        newErrors.location = 'Please select a location';
    }

    if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
    }

    setLoading(true);
    setApiError('');

    try {
        // Prepare transfer data
        const transferData = selectedAssets.map(asset => ({
            assetId: asset.id,
            organizationId: selectedOrganization,
            organizationLocationId: selectedLocation
        }));

        // Call transfer API
        const response = await api.post('asset/transfer', transferData);
        
        // Handle warnings if any assets were skipped
        if (response.data.warning && response.data.warning.length > 0) {
            const warningMessages = response.data.warning.join('\n');
            setApiError(`Transfer completed with warnings:\n${warningMessages}`);
        }

        // Call success callback (even if there were warnings, some assets were transferred)
        if (onTransferComplete) {
            onTransferComplete();
        }

        // Close modal and reset state only if no warnings, or handle warnings appropriately
        if (!response.data.warning || response.data.warning.length === 0) {
            handleClose();
        }
        // If there are warnings, you might want to keep the modal open to show the warnings
        // and let the user decide whether to close it or not

    } catch (error) {
        console.error('Transfer failed:', error);
        setApiError(error?.response?.data?.error || 'Failed to transfer assets');
    } finally {
        setLoading(false);
    }
};

    // Handle modal close
    const handleClose = () => {
        setSelectedOrganization('');
        setSelectedLocation('');
        setLocations([]);
        setErrors({});
        setApiError('');
        toggle();
    };

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchOrganizations();
            setSelectedOrganization('');
            setSelectedLocation('');
            setLocations([]);
            setErrors({});
            setApiError('');
        }
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            toggle={handleClose}
            centered
            className="fade-scale-modal"
            backdropClassName="modal-backdrop-blur"
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
            >
                <div className="modal-header">
                    <h5 className="modal-title">Transfer Assets</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={handleClose}
                        aria-label="Close"
                        disabled={loading}
                    ></button>
                </div>

                <div className="modal-body p-4">
                    {/* Selected Assets Summary */}
                    <div className="mb-4">
                        <h6 className="text-muted mb-2">
                            Transferring {selectedAssets.length} asset{selectedAssets.length !== 1 ? 's' : ''}:
                        </h6>
                        <div className="bg-light p-3 rounded" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                            {selectedAssets.map((asset, index) => (
                                <div key={asset.id} className="d-flex justify-content-between align-items-center py-1">
                                    <span className="small">{asset.assetName}</span>
                                    <span className="badge bg-secondary small">{asset.serialNumber}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Organization Selection */}
                    <Row className="mb-3">
                        <Col md={12}>
                            <div className="form-group">
                                <label htmlFor="transferOrganization" className="form-label fw-medium">
                                    Destination Organization <span className="text-danger">*</span>
                                </label>
                                <select
                                    id="transferOrganization"
                                    className={`form-select ${errors.organization ? 'is-invalid' : ''}`}
                                    value={selectedOrganization}
                                    onChange={handleOrganizationChange}
                                    disabled={loading}
                                >
                                    <option value="">Select Organization</option>
                                    {organizations.map((organization) => (
                                        <option key={organization.id} value={organization.id}>
                                            {organization.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.organization && (
                                    <div className="invalid-feedback">{errors.organization}</div>
                                )}
                            </div>
                        </Col>
                    </Row>

                    {/* Location Selection */}
                    <Row className="mb-3">
                        <Col md={12}>
                            <div className="form-group">
                                <label htmlFor="transferLocation" className="form-label fw-medium">
                                    Destination Location <span className="text-danger">*</span>
                                </label>
                                <select
                                    id="transferLocation"
                                    className={`form-select ${errors.location ? 'is-invalid' : ''}`}
                                    value={selectedLocation}
                                    onChange={handleLocationChange}
                                    disabled={loading || !selectedOrganization}
                                >
                                    <option value="">
                                        {selectedOrganization ? 'Select Location' : 'Select Organization First'}
                                    </option>
                                    {locations.map((location) => (
                                        <option key={location.id} value={location.id}>
                                            {location.locationName}
                                        </option>
                                    ))}
                                </select>
                                {errors.location && (
                                    <div className="invalid-feedback">{errors.location}</div>
                                )}
                            </div>
                        </Col>
                    </Row>

                    {/* API Error Display */}
                    {apiError && (
                        <div className="alert alert-danger" role="alert">
                            {apiError}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleTransfer}
                        disabled={loading || !selectedOrganization || !selectedLocation}
                    >
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Transferring...
                            </>
                        ) : (
                            'Transfer Assets'
                        )}
                    </button>
                </div>
            </motion.div>
        </Modal>
    );
};

export default TransferModal;